import { Clipboard } from '@angular/cdk/clipboard';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { APP_VERSION } from 'config-data';
import { parseVersion } from './version-parse';

/**
 * How the version is presented, derived from git-flow's branch type logic:
 * - `release`     mainline, no prerelease           → `vX.Y.Z`, default colour
 * - `prerelease`  mainline pre-release, no build no. → full version, orange
 * - `build`       development branch, or mainline
 *                 with a `.build.<n>` suffix         → red, two lines: `vX.Y.Z` / prerelease
 */
export type VersionDisplay = 'release' | 'prerelease' | 'build';

@Component({
  selector: 'ideal-version-info',
  standalone: true,
  imports: [MatButtonModule],
  templateUrl: './version-info.component.html',
  styleUrl: './version-info.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VersionInfoComponent {
  private readonly clipboard = inject(Clipboard);

  protected readonly appVersion = inject(APP_VERSION);
  protected readonly parsed = parseVersion(this.appVersion);
  protected readonly display: VersionDisplay = classify(this.parsed);

  protected readonly copied = signal(false);
  private copiedTimer?: ReturnType<typeof setTimeout>;

  protected copy(): void {
    if (!this.clipboard.copy(this.appVersion)) return;
    this.copied.set(true);
    clearTimeout(this.copiedTimer);
    this.copiedTimer = setTimeout(() => this.copied.set(false), 1500);
  }
}

function classify(parsed: ReturnType<typeof parseVersion>): VersionDisplay {
  if (!parsed) return 'build';
  if (parsed.branchType === 'development' || parsed.buildNumber !== undefined) return 'build';
  return parsed.isPreRelease ? 'prerelease' : 'release';
}
